import { observable, configure, action } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

import { setFaviconSource } from '../components/gui/faviconBadge';

configure({ enforceActions: 'always' });

class SupportTicketStore extends LocalModel {
    constructor() {
        super('supportTicket', {
            namePlural: 'supportTickets',
            namePluralReal: 'supportTickets',
            sort: '-lastMessageDate',
            limit: 50,
            api: {
                search: {
                    url: '/api/supporttickets/',
                    params: {
                        limit: 50,
                    },
                },
                load: {
                    url: '/api/supporttickets/',
                    params: {},
                },
                save: {
                    url: '/api/supporttickets/',
                    params: {},
                },
                delete: {
                    url: '/api/supporttickets/',
                    params: {},
                },
            },
        });
    }

    @observable ticket = {};
    @observable tickets = [];
    @observable ticketsTotal = 0;
    @observable staffQueue = {};
    @observable staffQueueCounts = {};
    @observable unreadCount = 0;
    @observable openCount = 0;
    @observable messageContent = '';
    @observable newTicketSubject = '';
    @observable newTicketBody = '';

    // Body text handed over from another page (help-center zero-hit search). Survives
    // resetNewTicket() so NewTicket can apply it after its own reset on mount.
    @observable newTicketPrefillBody = '';
    @observable newTicketCategory = 'general';
    @observable newTicketPriority = 'normal';
    @observable staffFilter = 'all';
    @observable staffSortBy = 'lastMessageDate';
    @observable staffSearch = '';
    @observable statistics = {};
    @observable statisticsLoading = false;
    @observable statisticsPeriod = 'month';
    @observable draftUuidv4 = null;
    @observable draftAiSolvedList = [];
    @observable draftSubmittedAnywayList = [];
    @observable draftListLoading = false;

    // AI draft analysis — detected entity types + user-picked related records
    @observable draftEntities = [];
    @observable draftRelated = {};
    analyzeSeq = 0;

    // IMAP email import
    @observable imapEmails = [];
    @observable imapLoading = false;
    @observable importingEmails = false;
    @observable showImportModal = false;

    // AI Customer Summary
    @observable customerSummary = null;
    @observable customerSummaryLoading = false;
    @observable databaseName = '';

    // Create ticket for user (staff)
    @observable showCreateTicketModal = false;
    @observable userSearchResults = [];
    @observable userSearchLoading = false;
    @observable selectedUser = null;
    @observable creatingTicket = false;

    categories = ['billing', 'technical', 'general', 'feature_request', 'bug_report'];
    priorities = ['low', 'normal', 'high', 'urgent'];
    statuses = ['open', 'in_progress', 'waiting_customer', 'later', 'resolved', 'closed'];

    @action
    consumeNewTicketPrefill() {
        const prefill = this.newTicketPrefillBody || '';
        this.newTicketPrefillBody = '';
        return prefill;
    }

    @action
    resetNewTicket() {
        this.newTicketSubject = '';
        this.newTicketBody = '';
        this.newTicketCategory = 'general';
        this.newTicketPriority = 'normal';
        this.draftUuidv4 = null;
        this.draftEntities = [];
        this.draftRelated = {};
    }

    async getUnreadCount(filters = {}) {
        const response = await util.fetchApi('/api/supporttickets/unread', { method: 'GET' }, filters);
        if (response.status === 200) {
            const newUnreadCount = response.data || 0;
            this.updateKeyValue('unreadCount', newUnreadCount);
            setFaviconSource('support', newUnreadCount);
        }
        return response;
    }

    async getOpenCount(filters = {}) {
        const response = await util.fetchApi('/api/supporttickets/open-count', { method: 'GET' }, filters);
        if (response.status === 200) {
            const newOpenCount = response.data || 0;
            this.updateKeyValue('openCount', newOpenCount);
            setFaviconSource('supportOpen', newOpenCount);
        }
        return response;
    }

    async getTickets(filters = {}, { append = false } = {}) {
        const response = await util.fetchApi('/api/supporttickets/', { method: 'GET' }, filters);
        if (response.status === 200) {
            const newTickets = response.data || [];
            // The backend caps the list at 50 per request while the unread badge counts
            // ALL non-closed tickets, so an old unread ticket can sit beyond the first
            // page. append + ticketsTotal lets the list load the rest ("Show more").
            this.updateKeyValue('tickets', append ? [...this.tickets, ...newTickets] : newTickets);
            this.updateKeyValue('ticketsTotal', response.pagination?.total || newTickets.length);
        }
        return response;
    }

    async getTicket(uuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}`, { method: 'GET' });
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            this.updateKeyValue('databaseName', response.databaseName || '');
        }
        return response;
    }

    async createTicket(subject, body, category = 'general', priority = 'normal', images = [], files = [], customer = null, race = null, sensitive = false) {
        const data = {
            body,
            category,
            priority,
            images,
            files,
            customer,
        };
        if (subject) {
            data.subject = subject;
        }
        if (race) {
            data.race = race;
        }
        if (sensitive) {
            data.sensitive = true;
        }
        if (this.draftUuidv4) {
            data.draftUuidv4 = this.draftUuidv4;
        }
        const response = await util.fetchApi('/api/supporttickets/', { method: 'POST' }, data);
        if (response.status === 201) {
            this.resetNewTicket();
            await this.getTickets();
            return response.data;
        }
        return null;
    }

    async sendReply(uuidv4, body, images = [], files = [], options = {}) {
        const payload = {
            body,
            images,
            files,
            onBehalfOfCustomer: options.onBehalfOfCustomer || false,
            inputSource: options.inputSource || null,
        };
        // When replying on behalf of customer, must explicitly confirm
        if (options.onBehalfOfCustomer) {
            payload.confirmOnBehalfOfCustomer = true;
        }
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/reply`, { method: 'POST' }, payload);
        if (response.status === 201) {
            this.updateKeyValue('ticket', response.data || {});
            this.updateKeyValue('messageContent', '');
            return response.data;
        }
        // Throw error with response details for handling in component
        const error = new Error(response.message || 'Failed to send reply');
        error.code = response.code;
        error.status = response.status;
        throw error;
    }

    async scheduleReply(uuidv4, { body, images = [], files = [], scheduledPublishDate, scheduleSendEmail = true, scheduleSendSms = false, smsBody = '' }) {
        const payload = { body, images, files, scheduledPublishDate, scheduleSendEmail, scheduleSendSms, smsBody };
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/schedule-reply`, { method: 'POST' }, payload);
        if (response.status === 201 || response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            this.updateKeyValue('messageContent', '');
        }
        return response;
    }

    // Draft (2026-09-09): same endpoint, status 'draft', no send time. Email stays the
    // default channel so "Send nå" delivers like a normal reply.
    async saveDraft(uuidv4, { body, images = [], files = [] }) {
        const payload = {
            status: 'draft', body, images, files, scheduleSendEmail: true, scheduleSendSms: false,
        };
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/schedule-reply`, { method: 'POST' }, payload);
        if (response.status === 201 || response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            this.updateKeyValue('messageContent', '');
        }
        return response;
    }

    async editScheduledReply(uuidv4, scheduledUuidv4, updates) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/scheduled-reply/${scheduledUuidv4}`, { method: 'PATCH' }, updates);
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
        }
        return response;
    }

    async cancelScheduledReply(uuidv4, scheduledUuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/scheduled-reply/${scheduledUuidv4}`, { method: 'DELETE' });
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
        }
        return response;
    }

    async publishScheduledReplyNow(uuidv4, scheduledUuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/scheduled-reply/${scheduledUuidv4}/publish-now`, { method: 'POST' });
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
        }
        return response;
    }

    async changeMessageSender(uuidv4, messageId, isStaffReply) {
        return this.updateTicket(uuidv4, {
            updateMessage: {
                messageId,
                isStaffReply,
            },
        });
    }

    async updateTicket(uuidv4, updates) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}`, { method: 'PATCH' }, updates);
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        return null;
    }

    // Update a ticket in the staffQueue list in place (without reload)
    updateTicketInQueue(uuidv4, updates) {
        const { staffQueue } = this;
        if (!staffQueue || !staffQueue.tickets) return;

        const tickets = staffQueue.tickets.map(ticket => {
            if (ticket.uuidv4 === uuidv4) {
                return { ...ticket, ...updates };
            }
            return ticket;
        });

        this.updateKeyValue('staffQueue', {
            ...staffQueue,
            tickets,
        });
    }

    // Remove a ticket from the staffQueue list (when it no longer matches filter)
    removeTicketFromQueue(uuidv4) {
        const { staffQueue } = this;
        if (!staffQueue || !staffQueue.tickets) return;

        const tickets = staffQueue.tickets.filter(ticket => ticket.uuidv4 !== uuidv4);

        this.updateKeyValue('staffQueue', {
            ...staffQueue,
            tickets,
        });
    }

    async getStaffQueue(filter = 'all', sortBy = 'lastMessageDate', limit = 50, offset = 0, customer = null, showAll = false, search = '', mineOnly = false) {
        const response = await util.fetchApi('/api/supporttickets/staff/queue', { method: 'GET' }, {
            filter,
            sortBy,
            limit,
            offset,
            customer,
            showAll,
            search,
            mineOnly,
        });
        if (response.status === 200) {
            this.updateKeyValue('staffQueue', {
                tickets: response.data || [],
                pagination: response.pagination || {},
            });
            this.updateKeyValue('staffQueueCounts', response.counts || {});
        }
        return response;
    }

    async getStatistics(period = 'month', customer = null, showAll = false) {
        this.updateKeyValue('statisticsLoading', true);
        this.updateKeyValue('statisticsPeriod', period);
        try {
            const response = await util.fetchApi('/api/supporttickets/staff/statistics', { method: 'GET' }, {
                period,
                customer,
                showAll,
            });
            if (response.status === 200) {
                this.updateKeyValue('statistics', response.data || {});
            }
            return response;
        } finally {
            this.updateKeyValue('statisticsLoading', false);
        }
    }

    async addInternalNote(uuidv4, body, details = {}) {
        const response = await util.fetchApi(
            `/api/supporttickets/${uuidv4}/note`, { method: 'POST' }, { ...details, body },
        );
        if (response.status === 201) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        throw new Error(response.message || 'Failed to add internal note.');
    }

    async editInternalNote(uuidv4, noteId, body, details = {}) {
        const response = await util.fetchApi(
            `/api/supporttickets/${uuidv4}/note/${noteId}`, { method: 'PATCH' }, { ...details, body },
        );
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        throw new Error(response.message || 'Failed to update internal note.');
    }

    async deleteInternalNote(uuidv4, noteId) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/note/${noteId}`, { method: 'DELETE' });
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        throw new Error(response.message || 'Failed to delete internal note.');
    }

    async assignToMe(uuidv4, currentUser) {
        return this.updateTicket(uuidv4, {
            assignedTo: currentUser.id,
            assignedToUuidv4: currentUser.uuidv4,
        });
    }

    async assignToUser(uuidv4, user) {
        return this.updateTicket(uuidv4, {
            assignedTo: user.id,
            assignedToUuidv4: user.uuidv4,
        });
    }

    async unassign(uuidv4) {
        return this.updateTicket(uuidv4, {
            assignedTo: 0,
        });
    }

    async changeStatus(uuidv4, status) {
        return this.updateTicket(uuidv4, { status });
    }

    // Send a ticket to "Later" with an optional deadline (ISO string or null).
    // laterDate null = defer with no date (stays in Later until acted on).
    async setLater(uuidv4, laterDate = null) {
        return this.updateTicket(uuidv4, { status: 'later', laterDate });
    }

    async changePriority(uuidv4, priority) {
        return this.updateTicket(uuidv4, { priority });
    }

    async changeSensitive(uuidv4, sensitive) {
        return this.updateTicket(uuidv4, { sensitive });
    }

    async changeCategory(uuidv4, category) {
        return this.updateTicket(uuidv4, { category });
    }

    async fixCustomer(uuidv4) {
        return this.updateTicket(uuidv4, { fixCustomer: true });
    }

    async changeUser(uuidv4, userId) {
        return this.updateTicket(uuidv4, { user: userId });
    }

    async changeCustomer(uuidv4, customerId) {
        return this.updateTicket(uuidv4, { customer: customerId });
    }

    async changeReplyToUser(uuidv4, userId) {
        return this.updateTicket(uuidv4, { replyToUser: userId });
    }

    async searchCustomers(query, isAdmin = false) {
        if (!query || query.length < 2) {
            return [];
        }
        const endpoint = isAdmin ? '/api/customers/admin/' : '/api/customers/';
        const response = await util.fetchApi(endpoint, { method: 'GET' }, { search: query, limit: 20 });
        if (response.status === 200) {
            return response.data || [];
        }
        return [];
    }

    // Knowledge Base search methods
    async searchArticles(query) {
        if (!query || query.length < 2) {
            return [];
        }
        // Always search the domain help center (same scoping as components/help/index.js):
        // get-article.js resolves `hostname` to the domain customer. Without it the backend
        // falls back to the logged-in user's own customer memberships, so support staff who
        // are not members of the customer owning the help articles get zero hits.
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const response = await util.fetchApi('/api/articles/', { method: 'GET' }, {
            search: query,
            limit: 10,
            hostname,
            category: 'help',
        });
        if (response.status === 200) {
            return response.data || [];
        }
        return [];
    }

    async searchTicketAnswers(query) {
        if (!query || query.length < 2) {
            return [];
        }
        const response = await util.fetchApi('/api/supporttickets/search-answers', { method: 'GET' }, { search: query, limit: 10 });
        if (response.status === 200) {
            return response.data || [];
        }
        return [];
    }

    async updateTags(uuidv4, tags) {
        return this.updateTicket(uuidv4, { tags });
    }

    async addTag(uuidv4, tag) {
        const currentTags = this.ticket.tags || [];
        if (!currentTags.includes(tag)) {
            return this.updateTicket(uuidv4, { tags: [...currentTags, tag] });
        }
        return this.ticket;
    }

    async removeTag(uuidv4, tag) {
        const currentTags = this.ticket.tags || [];
        return this.updateTicket(uuidv4, { tags: currentTags.filter(t => t !== tag) });
    }

    async regenerateTags(uuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/regenerate-tags`, { method: 'POST' });
        if (response && response.status === 200 && response.data) {
            // Update current ticket in store with new tags
            const updated = { ...(this.ticket || {}), tags: response.data.tags || [] };
            this.updateKeyValue('ticket', updated);
            return response.data;
        }
        return null;
    }

    async suggestReply(uuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/suggest-reply`, { method: 'POST' });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async rewriteReply(uuidv4, draft) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/rewrite-reply`, { method: 'POST' }, { draft });
        if (response.status === 200) {
            return response.data?.rewrittenText || draft;
        }
        return draft;
    }

    async regenerateAiResponse(uuidv4) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/regenerate-ai`, { method: 'POST' });
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        return null;
    }

    async generateCustomerSummary(uuidv4, forceRegenerate = false) {
        this.updateKeyValue('customerSummaryLoading', true);
        try {
            const response = await util.fetchApi(
                `/api/supporttickets/${uuidv4}/customer-summary`,
                { method: 'POST' },
                { forceRegenerate },
            );
            if (response.status === 200) {
                this.updateKeyValue('customerSummary', response.data || null);
                return response.data;
            }
            return null;
        } finally {
            this.updateKeyValue('customerSummaryLoading', false);
        }
    }

    // IMAP email import methods
    async fetchImapEmails() {
        this.updateKeyValue('imapLoading', true);
        this.updateKeyValue('imapEmails', []);
        try {
            const response = await util.fetchApi('/api/supporttickets/imap/emails', { method: 'GET' });
            if (response.status === 200) {
                this.updateKeyValue('imapEmails', response.data?.emails || []);
                return response.data;
            }
            return null;
        } finally {
            this.updateKeyValue('imapLoading', false);
        }
    }

    async importEmails(emailUids) {
        this.updateKeyValue('importingEmails', true);
        try {
            const response = await util.fetchApi('/api/supporttickets/imap/import', { method: 'POST' }, { emailUids });
            if (response.status === 200) {
                // Clear imported emails from the list
                const importedUids = (response.data?.imported || []).map(i => i.uid);
                const remaining = this.imapEmails.filter(e => !importedUids.includes(e.uid));
                this.updateKeyValue('imapEmails', remaining);
                return response.data;
            }
            return null;
        } finally {
            this.updateKeyValue('importingEmails', false);
        }
    }

    async sendEmailReply(uuidv4, messageIndex = -1) {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/send-email`, { method: 'POST' }, { messageIndex });
        if (response.status === 200) {
            // Delay for replicated database sync
            await new Promise(resolve => setTimeout(resolve, 1000));
            // Refresh ticket to get updated message status
            await this.getTicket(uuidv4);
            return response.data;
        }
        return null;
    }

    async sendSmsReply(uuidv4, messageIndex, smsBody) {
        const response = await util.fetchApi(
            `/api/supporttickets/${uuidv4}/send-sms`,
            { method: 'POST' },
            { messageIndex, smsBody },
        );
        if (response.status === 200) {
            // Delay for replicated database sync
            await new Promise(resolve => setTimeout(resolve, 1000));
            // Refresh ticket to get updated message status
            await this.getTicket(uuidv4);
            return response.data;
        }
        return null;
    }

    async forwardMessage(uuidv4, messageIndex, emails, note = '') {
        const response = await util.fetchApi(`/api/supporttickets/${uuidv4}/forward`, { method: 'POST' }, {
            messageIndex,
            emails,
            note,
        });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    @action
    toggleImportModal(show = null) {
        this.showImportModal = show !== null ? show : !this.showImportModal;
    }

    // Create ticket for user (staff) methods
    @action
    toggleCreateTicketModal(show = null) {
        this.showCreateTicketModal = show !== null ? show : !this.showCreateTicketModal;
        if (!this.showCreateTicketModal) {
            // Reset state when closing
            this.userSearchResults = [];
            this.selectedUser = null;
        }
    }

    @action
    setSelectedUser(user) {
        this.selectedUser = user;
    }

    @action
    clearSelectedUser() {
        this.selectedUser = null;
    }

    async searchUsersForTicket(query) {
        if (!query || query.length < 2) {
            this.updateKeyValue('userSearchResults', []);
            return [];
        }

        this.updateKeyValue('userSearchLoading', true);
        try {
            const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search: query });
            if (response.status === 200) {
                this.updateKeyValue('userSearchResults', response.data || []);
                return response.data || [];
            }
            return [];
        } finally {
            this.updateKeyValue('userSearchLoading', false);
        }
    }

    async createTicketForUser({ userUuidv4, subject, body, category, priority, images, files, customer }) {
        this.updateKeyValue('creatingTicket', true);
        try {
            const response = await util.fetchApi('/api/supporttickets/staff/create', { method: 'POST' }, {
                userUuidv4,
                subject,
                body,
                category,
                priority,
                images,
                files,
                customer,
            });
            if (response.status === 201) {
                this.toggleCreateTicketModal(false);
                return response.data;
            }
            return null;
        } finally {
            this.updateKeyValue('creatingTicket', false);
        }
    }

    async bulkChangeStatus(fromStatus, toStatus, olderThanDays) {
        const response = await util.fetchApi('/api/supporttickets/staff/bulk-status', { method: 'POST' }, {
            fromStatus,
            toStatus,
            olderThanDays,
        });
        if (response.status === 200) {
            return response.data;
        }
        return null;
    }

    async getSuggestion(body, category) {
        const payload = { body, category };
        if (this.draftUuidv4) payload.draftUuidv4 = this.draftUuidv4;
        const response = await util.fetchApi('/api/supporttickets/suggest-answer', { method: 'POST' }, payload);
        return response?.data;
    }

    // Advisory: detect which record types the draft refers to. Never throws.
    async analyzeDraft(body, category) {
        this.analyzeSeq += 1;
        const seq = this.analyzeSeq;
        try {
            const response = await util.fetchApi(
                '/api/supporttickets/analyze-draft',
                { method: 'POST' },
                { body, category },
            );
            if (seq !== this.analyzeSeq) return; // a newer analysis superseded this one
            const entities = (response && response.data && response.data.entities) || [];
            this.updateKeyValue('draftEntities', entities);
        } catch (err) {
            // analysis is advisory — ignore failures
        }
    }

    @action
    addRelatedRecord(type, record) {
        const existing = this.draftRelated[type] || [];
        if (existing.some(r => r.id === record.id)) return;
        this.draftRelated = { ...this.draftRelated, [type]: [...existing, record] };
    }

    @action
    removeRelatedRecord(type, recordId) {
        const existing = this.draftRelated[type] || [];
        this.draftRelated = { ...this.draftRelated, [type]: existing.filter(r => r.id !== recordId) };
    }

    async createDraft({ category, priority, race } = {}) {
        if (this.draftUuidv4) return this.draftUuidv4;
        try {
            const response = await util.fetchApi(
                '/api/supportticketdrafts/',
                { method: 'POST' },
                {
                    category: category || 'general',
                    priority: priority || 'normal',
                    race: race || null,
                },
            );
            if (response?.status === 201 && response.data?.uuidv4) {
                this.updateKeyValue('draftUuidv4', response.data.uuidv4);
                return response.data.uuidv4;
            }
        } catch (err) {
            console.error('createDraft failed:', err.message);
        }
        return null;
    }

    async updateDraft(fields = {}) {
        if (!this.draftUuidv4) return;
        try {
            await util.fetchApi(
                `/api/supportticketdrafts/${this.draftUuidv4}`,
                { method: 'PATCH' },
                fields,
            );
        } catch (err) {
            console.error('updateDraft failed:', err.message);
        }
    }

    async setDraftOutcome(outcome) {
        if (!this.draftUuidv4) return;
        try {
            await util.fetchApi(
                `/api/supportticketdrafts/${this.draftUuidv4}`,
                { method: 'PATCH' },
                { outcome },
            );
        } catch (err) {
            console.error('setDraftOutcome failed:', err.message);
        }
    }

    sendDraftOutcomeBeacon(outcome) {
        if (!this.draftUuidv4) return;
        try {
            const apiServer = util.getApiServer ? util.getApiServer() : '';
            const jwtToken = util.getJwtToken ? util.getJwtToken() : null;
            const qs = jwtToken ? `?jwtToken=${encodeURIComponent(jwtToken)}` : '';
            const url = `${apiServer}/api/supportticketdrafts/${this.draftUuidv4}${qs}`;
            if (window.fetch) {
                fetch(url, {
                    method: 'PATCH',
                    keepalive: true,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ outcome }),
                }).catch(() => {});
            }
        } catch (err) {
            // best-effort
        }
    }

    async getDraftList(outcome, period = 'month', customer = null, showAll = false) {
        this.updateKeyValue('draftListLoading', true);
        try {
            const response = await util.fetchApi(
                '/api/supportticketdrafts/',
                { method: 'GET' },
                {
                    outcome, period, customer, showAll, limit: 20,
                },
            );
            if (response.status === 200) {
                if (outcome === 'ai_solved') {
                    this.updateKeyValue('draftAiSolvedList', response.data || []);
                } else if (outcome === 'submitted_anyway') {
                    this.updateKeyValue('draftSubmittedAnywayList', response.data || []);
                }
            }
        } finally {
            this.updateKeyValue('draftListLoading', false);
        }
    }

    async customerResolve(uuidv4, aiWasHelpful = false) {
        const response = await util.fetchApi(
            `/api/supporttickets/${uuidv4}`,
            { method: 'PATCH' },
            { status: 'resolved', aiResponseHelpful: aiWasHelpful },
        );
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        return null;
    }

    async customerReopen(uuidv4) {
        const response = await util.fetchApi(
            `/api/supporttickets/${uuidv4}`,
            { method: 'PATCH' },
            { status: 'open' },
        );
        if (response.status === 200) {
            this.updateKeyValue('ticket', response.data || {});
            return response.data;
        }
        return null;
    }

    @action
    cleanup() {
        this.ticket = {};
        this.tickets = [];
        this.messageContent = '';
        this.customerSummary = null;
        this.customerSummaryLoading = false;
        this.resetNewTicket();
    }
}

const store = new SupportTicketStore();
export default store;
